import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PanelMenu } from "primereact/panelmenu";
import { useNavigate } from "react-router-dom";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "primereact/resources/primereact.min.css";
import "./SideNav.scss";
import hspLogo from "../../assets/hsp-logo.svg";
import { Avatar } from "primereact/avatar";
import { useSelector } from "react-redux";
import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";
import { Badge } from "primereact/badge";
import { Skeleton } from "primereact/skeleton";
import { Inbox } from "@novu/react";
import Config from "../../config/env";
import { UserRole } from "../../types/roles";

export default function TemplateDemo({
  isPanelCollapsed,
  setShowNotificationCenter,
}) {
  const subscriberId = useSelector((state) => state.login.subscriber);
  const applicationIdentifier = Config.NOVU_APP_IDENTIFIER;

  const location = useLocation();

  const unreadNotificationCounter = useSelector(
    (state) => state.login.unreadNotificationCount,
  );
  const navigate = useNavigate();
  const { firstName, lastName, email, role } = useSelector(
    (state) => state.login,
  );
  const { value: isLspFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.LSP,
    false,
  );
  const { value: isNotificationsFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.NOTIFICATIONS,
    false,
  );
  const { value: isRequestsFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.REQUESTS,
    false,
  );
  const { value: isEscalationsFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.ESCALATIONS,
    false,
  );
  const { value: isMyCalendarFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.MY_CALENDAR,
    false,
  );

  const { value: isMyDashboardFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.MY_DASHBOARD,
    false,
  );
  const { value: isTenantFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.TENANT,
    false,
  );

  const isPanelActive = location.pathname.split("/");
  const activeTab = (item) => {
    if (isPanelActive[1] === "") {
      const routeSegment = item.route.split("/")[1];
      if (isMyDashboardFeatureEnabled && routeSegment === "my-dashboard") {
        return true;
      }

      if (!isMyDashboardFeatureEnabled && routeSegment === "entities") {
        return true;
      }
    }
    if (item.route.split("/")[1] === isPanelActive[1]) {
      return true;
    }
    if (item.route === "/entities") {
      if (
        isPanelActive[1] === "entity" ||
        isPanelActive[1] === "directors" ||
        isPanelActive[1] === "share-holders" ||
        isPanelActive[2] === "directors" ||
        isPanelActive[1] === "officers" ||
        isPanelActive[1] === "powers-of-attorney"
      ) {
        return true;
      }
    }
    if (item.route === "/tenants") {
      if (isPanelActive[1] === "tenants") {
        return true;
      }
    }
  };

  const itemRenderer = (item) => {
    activeTab(item);
    return (
      <Link
        to={item.route}
        className={`${activeTab(item) ? "active" : "inactive"} link flex align-items-center w-276px h-56px p-3 cursor-pointer`}
      >
        <span className={`${item.icon} `} />
        {!isPanelCollapsed && (
          <span className={`mx-2 ${item.items && "font-semibold"} label-text`}>
            {item.label}
          </span>
        )}
      </Link>
    );
  };

  const NotificationSidenav = (item) => {
    return (
      <div
        className="notification-container"
        onClick={() => setShowNotificationCenter(true)}
      >
        {subscriberId ? (
          <div className="notification">
            <div className=" flex align-items-center w-276px h-56px p-3 cursor-pointer">
              <span className={`${item.icon} `} />
              {!isPanelCollapsed && (
                <span
                  className={`mx-2 ${item.items && "font-semibold"} label-text`}
                >
                  {item.label}
                </span>
              )}
            </div>

            {unreadNotificationCounter > 0 && (
              <Badge value={unreadNotificationCounter} />
            )}

            <Inbox
              applicationIdentifier={applicationIdentifier}
              subscriber={subscriberId}
              renderBell={(unreadCount) => {
                // if unreadCount is smaller than 1, return null
                if (unreadCount.total < 1) {
                  return null;
                }
                // if unreadCount is greater than 99, return 99+
                if (unreadCount.total > 99) {
                  return <Badge value="99+" />;
                }
                return <Badge value={unreadCount.total} />;
              }}
            />
          </div>
        ) : (
          <Skeleton className="ml-3 notification-skeleton" />
        )}
      </div>
    );
  };

  const menuitems = () => {
    const items = [
      ...(isMyDashboardFeatureEnabled
        ? [
            {
              label: "Dashboard",
              icon: "pi pi-th-large",
              shortcut: "⌘+W",
              template: itemRenderer,
              route: "/my-dashboard",
              allowedRoles: [
                UserRole.GXM_ADMIN,
                UserRole.GXM_USER,
                UserRole.GXM_MANAGER,
                UserRole.CUSTOMER,
                UserRole.CUSTOMER_MANAGER,
                UserRole.LSP_USER,
                UserRole.LSP_MANAGER,
              ],
            },
          ]
        : []),
      ...(isTenantFeatureEnabled
        ? [
            {
              label: "Tenants",
              icon: "pi pi-sitemap",
              shortcut: "⌘+T",
              template: itemRenderer,
              route: "/tenants",
              allowedRoles: [UserRole.GXM_ADMIN, UserRole.SUPER_ADMIN],
            },
          ]
        : []),
      {
        label: "My Entities",
        icon: "pi pi-globe",
        shortcut: "⌘+W",
        template: itemRenderer,
        route: "/entities",
        allowedRoles: [
          UserRole.GXM_ADMIN,
          UserRole.GXM_USER,
          UserRole.GXM_MANAGER,
          UserRole.CUSTOMER,
          UserRole.CUSTOMER_MANAGER,
          UserRole.LSP_USER,
          UserRole.LSP_MANAGER,
        ],
      },
      ...(isLspFeatureEnabled
        ? [
            {
              label: "Local Teams",
              icon: "pi pi-compass",
              shortcut: "⌘+R",
              template: itemRenderer,
              route: "/lsp",
              allowedRoles: [UserRole.GXM_ADMIN, UserRole.SUPER_ADMIN],
            },
          ]
        : []),
      ...(isRequestsFeatureEnabled
        ? [
            {
              label: "Requests",
              icon: "pi pi-comments",
              shortcut: "⌘+W",
              template: itemRenderer,
              route: "/requests",
              allowedRoles: [
                UserRole.GXM_ADMIN,
                UserRole.GXM_USER,
                UserRole.GXM_MANAGER,
                UserRole.CUSTOMER,
                UserRole.CUSTOMER_MANAGER,
                UserRole.LSP_USER,
                UserRole.LSP_MANAGER,
              ],
            },
          ]
        : []),
      ...(isEscalationsFeatureEnabled
        ? [
            {
              label: "Escalations",
              icon: "pi pi-briefcase",
              shortcut: "⌘+W",
              template: itemRenderer,
              route: "/escalations",
              allowedRoles: [
                UserRole.GXM_ADMIN,
                UserRole.GXM_USER,
                UserRole.GXM_MANAGER,
                UserRole.CUSTOMER,
                UserRole.CUSTOMER_MANAGER,
              ],
            },
          ]
        : []),
      ...(isMyCalendarFeatureEnabled
        ? [
            {
              label: "My Calendar",
              icon: "pi pi-calendar",
              shortcut: "⌘+W",
              template: itemRenderer,
              route: "/calendar",
              allowedRoles: [
                UserRole.GXM_ADMIN,
                UserRole.GXM_USER,
                UserRole.GXM_MANAGER,
                UserRole.CUSTOMER,
                UserRole.CUSTOMER_MANAGER,
                UserRole.LSP_USER,
                UserRole.LSP_MANAGER,
              ],
            },
          ]
        : []),
      {
        label: "Settings",
        icon: "pi pi-cog",
        shortcut: "⌘+W",
        template: itemRenderer,
        route: "/settings/my-profile",
        allowedRoles: [],
      },
      ...(isNotificationsFeatureEnabled
        ? [
            {
              label: "Notifications",
              icon: "pi pi-bell",
              shortcut: "⌘+W",
              template: NotificationSidenav,
              allowedRoles: [
                UserRole.GXM_ADMIN,
                UserRole.GXM_USER,
                UserRole.GXM_MANAGER,
                UserRole.CUSTOMER,
                UserRole.CUSTOMER_MANAGER,
                UserRole.LSP_USER,
                UserRole.LSP_MANAGER,
              ],
            },
          ]
        : []),
    ];
    return items.filter((item) => {
      // If allowedRoles is empty, show to all roles
      if (item.allowedRoles.length === 0) {
        return true;
      }
      // Otherwise, check if current role is in allowedRoles
      return item.allowedRoles.includes(role);
    });
  };

  const handleProfile = () => {
    navigate("/settings/my-profile");
  };
  const getInitials = (fullName) => {
    const nameArray = fullName.split(" ");
    const firstInitial = nameArray[0] ? nameArray[0].charAt(0) : "";
    const lastInitial =
      nameArray.length > 1 ? nameArray[nameArray.length - 1].charAt(0) : "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <>
      <div>
        {!isPanelCollapsed && (
          <div
            className={`card flex justify-content-start mb-3 ${isPanelCollapsed ? "logo-display" : ""}`}
          >
            <img alt="logo" src={hspLogo} className="mr-2"></img>
          </div>
        )}
        <div className="card flex justify-content-center">
          <PanelMenu
            model={menuitems()}
            className="w-full bg-white md:w-20rem"
          />
        </div>
      </div>
      {!isPanelCollapsed && (
        <div
          onClick={() => handleProfile()}
          className={`card flex justify-content-start align-items-end cursor-pointer ${isPanelCollapsed ? "logo-display" : ""}`}
        >
          <Avatar
            label={getInitials(`${firstName} ${lastName}`)}
            // image="https://primefaces.org/cdn/primereact/images/avatar/amyelsner.png"
            shape="circle"
          />
          <div className="profile-container ml-2">
            <p className="profile-name mb-1">
              {[firstName, lastName].filter(Boolean).join(" ")}
            </p>
            <p className="profile-email">{email}</p>
          </div>
        </div>
      )}
    </>
  );
}
